import { DestinationPushStatus } from '_/features/destinationPushes/common/constants';
import { SnapshotStatus } from '_/features/snapshots/common/snapshot.constants';
import formatPercentage from '_/utils/format/formatPercentage';
import Row from 'antd/lib/row';
import clsx from 'clsx';
import React, { ReactElement } from 'react';

import classes from './DeliveryGridItem.module.css';
import { IDeliveryGridItemProps, IDeliveryGridItemRenderProps, IDeliveryGridItemTileMeta } from './DeliveryGridItem.types';
import DeliveryGridItemTile from './DeliveryGridItemTile';

const successPercent = 95;

const suffix = '%';

const tiles: IDeliveryGridItemTileMeta[] = [
  {
    key: 0,
    title: 'Success',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const health = stats?.health || {};
      const okPct = health.okPct?.weightedAvg || 0;
      const notFoundPct = health.notFoundPct?.weightedAvg || 0;
      const noDataPct = health.noData200Pct?.weightedAvg || 0;

      const value = formatPercentage(okPct + notFoundPct - noDataPct);
      const className = value >= successPercent ? classes.successBackground : classes.successCardBorder;
      return { cardClass: className, suffix: suffix, value: value };
    },
  },
  {
    key: 1,
    title: 'Total Snapshots',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const statusList = stats?.status;
      const value = statusList ? Object.values(statusList).reduce((a, b) => a + b, 0) : 0;
      return { value: value };
    },
  },
  {
    key: 2,
    title: 'Snapshots Pushed / Expected',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const { pushedOnTime = '0', shouldHaveBeenPushed = '0' } = stats?.timeliness || {};
      return { suffix: `/ ${shouldHaveBeenPushed}`, value: pushedOnTime };
    },
  },
  {
    key: 3,
    title: 'Failed Starts / No Logins',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      let { [SnapshotStatus.START_FAILED]: failedStarts, [SnapshotStatus.NO_LOGIN_AVAILABLE]: noLogins } = stats?.status || {};

      failedStarts ||= 0;
      noLogins ||= 0;
      return { value: failedStarts + noLogins, suffix: '/ ' + noLogins };
    },
  },
  {
    key: 4,
    title: 'Failed Imports',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => ({
      value: stats?.status?.[SnapshotStatus.FAILED] || 0,
    }),
  },
  {
    key: 5,
    title: 'Inputs Collected / Total',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const { collected, total } = stats?.progress || { collected: 0, total: 0 };
      return { suffix: `/ ${total}`, value: collected };
    },
  },
  {
    key: 6,
    title: 'Rows Extracted',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => ({
      value: stats?.health?.rows?.sum || 0,
    }),
  },
  {
    key: 7,
    title: 'Push Errors',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const value =
        (stats?.pushes?.[DestinationPushStatus.FAILED] || 0) + (stats?.pushes?.[DestinationPushStatus.INTERNAL_FAILURE] || 0);
      const statisticClass = clsx(classes.statistic, { [classes.error]: value });
      return { statisticClass: statisticClass, value: value };
    },
  },
  {
    key: 8,
    title: '404/410 Responses',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => ({
      suffix: suffix,
      value: formatPercentage(stats?.health?.notFoundPct?.weightedAvg),
    }),
  },
  {
    key: 9,
    title: 'Rows Not Generated (with 200)',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => ({
      suffix: suffix,
      value: formatPercentage(stats?.health?.noData200Pct?.weightedAvg),
    }),
  },
  {
    key: 10,
    title: 'Sources Trimmed / Total Sources',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const { total, trimmed } = stats?.sources || { total: 0, trimmed: 0 };
      return { suffix: `/ ${total}`, value: trimmed };
    },
  },
  {
    key: 11,
    title: 'Inputs Errors / Inputs Blocked',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const { blockedPct, errorPct } = stats?.health || {};
      const blocked = formatPercentage(blockedPct?.weightedAvg);
      const value = formatPercentage(errorPct?.weightedAvg);

      const valueCustomRender = () => (
        <span className={classes.centerText}>
          <span className={classes.statistic}>{value}</span>
          &nbsp;
          <span className={classes.suffix}>%</span>
        </span>
      );

      return { suffix: `/ ${blocked} %`, value: value, valueCustomRender: valueCustomRender };
    },
  },
  {
    key: 12,
    title: 'Missing HTML',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => ({
      suffix: suffix,
      value: formatPercentage(stats?.health?.noHtmlPct?.weightedAvg),
    }),
  },
  {
    key: 13,
    title: '',
    getProps: ({ stats }, flow): IDeliveryGridItemRenderProps => {
      const collectHours = flow?.definition?.chunkCollectHours;
      const customTitle = collectHours
        ? `Inputs Collected in ${collectHours}hr / Before Timeout`
        : 'Inputs Collected on Time / Before Timeout';

      const value = formatPercentage(stats?.health?.collectedByPct?.weightedAvg);
      const collectedBeforeTimeoutPct = value === 0 ? 0 : 100 - formatPercentage(stats?.health?.timedOutPct?.weightedAvg);

      const valueCustomRender = () => (
        <span className={classes.centerText}>
          <span className={classes.statistic}>{value}</span>
          &nbsp;
          <span className={classes.suffix}>%</span>
        </span>
      );

      return {
        customTitle: customTitle,
        suffix: `/ ${collectedBeforeTimeoutPct} %`,
        value: value,
        valueCustomRender: valueCustomRender,
      };
    },
  },
  {
    key: 14,
    title: 'Inputs Process Rate',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => ({
      suffix: suffix,
      value: formatPercentage(stats?.progress?.expectedPct),
    }),
  },
  {
    key: 15,
    title: 'Source Duplicates',
    getProps: ({ stats }): IDeliveryGridItemRenderProps => {
      const statisticClass = clsx(classes.statistic, { [classes.error]: stats?.duplicates > 0 });
      return { statisticClass: statisticClass, value: stats?.dupes || 0 };
    },
  },
];

function DeliveryGridItem({ delivery, flow }: IDeliveryGridItemProps): ReactElement {
  return (
    <Row>
      {tiles.map(({ colSpan, getProps, key, title }) => (
        <DeliveryGridItemTile
          colSpan={colSpan}
          deliveryStats={delivery}
          flow={flow}
          getProps={getProps}
          key={key}
          title={title}
        />
      ))}
    </Row>
  );
}

export default DeliveryGridItem;
